/**
 * TanStack Query wiring for the Stock section.
 *
 * Stock is in the `live` freshness class, so lists refetch every 30 seconds while the tab is
 * visible and the `/changes` poller invalidates the `['stock']` root on a `stock.changed`
 * event. Nothing here polls on its own timer beyond that.
 *
 * Optimism is allowed for the count and the visible flag: neither moves money and neither
 * leaves the building, and both are compare and set, so a stale write is refused rather than
 * applied. A price edit waits for the server, per DESIGN.md section 6, because optimism on a
 * write that reaches the storefront is how a wrong price is live for ten seconds.
 *
 * A mutation invalidates the page and the row it touched, not the whole `['stock']` root. The
 * root belongs to the /changes poller, which is the one thing allowed to refresh a screen the
 * operator is not looking at.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  StockSetPriceInlineRequest,
  StockSetQtyRequest,
  StockSetVisibleRequest,
} from '@elutide/shared';
import { qk } from '@/lib/keys';
import { stockApi } from './api';
import type { AnyStockPageResponse, AnyStockPageRow, StockFilters } from './types';

const LIVE = { refetchInterval: 30_000, staleTime: 60_000 } as const;

/**
 * Every key in this file starts with the literal 'stock', which is what lets the /changes
 * poller reach it with one `invalidateQueries({ queryKey: ['stock'] })` on a stock.changed
 * event. A key that starts with anything else is a screen that goes quietly stale.
 */
export const stockPageKey = ['stock', 'page'] as const;
export const stockLegendKey = ['stock', 'legend'] as const;
export const stockRowDetailKey = (productId: number) =>
  ['stock', String(productId), 'detail'] as const;
export const stockWacBreakdownKey = (productId: number) =>
  ['stock', String(productId), 'wac-breakdown'] as const;

/** The whole overview in one request. Live class: 30 second poll, stale at 60. */
export function useStockPage() {
  return useQuery({
    queryKey: stockPageKey,
    queryFn: () => stockApi.page(),
    refetchInterval: 30_000,
    staleTime: 60_000,
  });
}

export function useStockRowDetail(productId: number | null) {
  return useQuery({
    queryKey: stockRowDetailKey(productId ?? 0),
    queryFn: () => stockApi.rowDetail(productId as number),
    enabled: productId !== null,
  });
}

/**
 * The batches behind one WAC.
 *
 * Enabled only while the popout is open, so clicking a cost costs one request and closing it
 * costs nothing.
 *
 * `staleTime: 0` on purpose. The batch cost write paths bump the facts version but emit no
 * `stock.changed`, so the /changes poller does not reach this key, while `/stock/page` polls
 * itself every thirty seconds. A cached breakdown would therefore be able to contradict the
 * row it was opened from. Refetching on open is one request per click and the click is rare.
 */
export function useWacBreakdown(productId: number | null) {
  return useQuery({
    queryKey: stockWacBreakdownKey(productId ?? 0),
    queryFn: () => stockApi.wacBreakdown(productId as number),
    enabled: productId !== null,
    staleTime: 0,
  });
}

/**
 * The definitions. They change when the owner settles a pending default, not every minute,
 * so this one does not poll. A missing legend leaves the popovers off rather than breaking
 * the screen.
 */
export function useLegend() {
  return useQuery({
    queryKey: stockLegendKey,
    queryFn: () => stockApi.legend(),
    staleTime: 600_000,
  });
}

/** Rewrites one row inside the cached page, families and hidden list alike. */
function patchPageRow(
  page: AnyStockPageResponse,
  productId: number,
  patch: (row: AnyStockPageRow) => AnyStockPageRow,
): AnyStockPageResponse {
  // The page is one shape or the other for its whole life: a patch never adds money to a
  // packer's row or takes it from the owner's, so the cast only restates what is true.
  return {
    ...page,
    families: page.families.map((family) => ({
      ...family,
      rows: family.rows.map((row) => (row.product_id === productId ? patch(row) : row)),
    })),
    hidden: page.hidden.map((row) => (row.product_id === productId ? patch(row) : row)),
  } as AnyStockPageResponse;
}

/**
 * Inline stock edit, optimistic with a rollback.
 *
 * DESIGN.md section 6 allows optimism here: a count moves no money and leaves no building.
 * The 409 path is the interesting one. The caller reverts to the number the server reports
 * rather than to the number the operator typed, because the point of compare and set is that
 * somebody else was right.
 */
export function useSetQty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { productId: number; body: StockSetQtyRequest }) =>
      stockApi.setQty(input.productId, input.body),

    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: stockPageKey });
      const previous = queryClient.getQueryData<AnyStockPageResponse>(stockPageKey);
      if (previous) {
        queryClient.setQueryData<AnyStockPageResponse>(
          stockPageKey,
          patchPageRow(previous, input.productId, (row) => ({ ...row, on_hand: input.body.qty })),
        );
      }
      return { previous };
    },

    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(stockPageKey, context.previous);
    },

    onSettled: (_result, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: stockPageKey });
      void queryClient.invalidateQueries({ queryKey: stockRowDetailKey(input.productId) });
    },
  });
}

/**
 * Inline price edit.
 *
 * Not optimistic. DESIGN.md section 6 forbids optimism on anything that moves money or leaves
 * the building, and a price is both: it is on the storefront the moment the bridge writes it.
 * The cell shows a pending state, waits, and renders the row the server sends back.
 *
 * What replaces the old confirm dialog is not optimism, it is Undo: the response carries a
 * fresh `woo_updated_at`, so writing the previous price back is a real compare and set rather
 * than a hopeful second attempt against a stale version.
 */
export function useSetPriceInline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { productId: number; body: StockSetPriceInlineRequest }) =>
      stockApi.setPriceInline(input.productId, input.body),

    onSuccess: (row, input) => {
      // Render the server's row rather than the typed number, so a price the bridge adjusted
      // shows as the bridge left it.
      const current = queryClient.getQueryData<AnyStockPageResponse>(stockPageKey);
      if (current && row) {
        queryClient.setQueryData<AnyStockPageResponse>(
          stockPageKey,
          patchPageRow(current, input.productId, () => row),
        );
      }
    },

    onSettled: (_row, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: stockPageKey });
      void queryClient.invalidateQueries({ queryKey: stockRowDetailKey(input.productId) });
    },
  });
}

/** The visible flag. Optimistic with a rollback, and an Undo on the toast. */
export function useSetVisible() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { productId: number; body: StockSetVisibleRequest }) =>
      stockApi.setVisible(input.productId, input.body),

    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: stockPageKey });
      const previous = queryClient.getQueryData<AnyStockPageResponse>(stockPageKey);
      if (previous) {
        queryClient.setQueryData<AnyStockPageResponse>(
          stockPageKey,
          patchPageRow(previous, input.productId, (row) => ({
            ...row,
            is_visible: input.body.is_visible,
          })),
        );
      }
      return { previous };
    },

    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(stockPageKey, context.previous);
    },

    onSettled: (_result, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: stockPageKey });
      void queryClient.invalidateQueries({ queryKey: stockRowDetailKey(input.productId) });
    },
  });
}

export function useStockList(filters: StockFilters) {
  return useQuery({
    queryKey: qk.stock(filters),
    queryFn: () => stockApi.list(filters),
    ...LIVE,
  });
}

export function useStockSummary() {
  return useQuery({
    queryKey: [...qk.stock('summary')],
    queryFn: () => stockApi.summary(),
    ...LIVE,
  });
}

export function useStockDetail(productId: number, enabled = true) {
  return useQuery({
    queryKey: [...qk.stock({ productId }), 'detail'],
    queryFn: () => stockApi.detail(productId),
    enabled,
  });
}

export function useLedger(productId: number) {
  return useQuery({
    queryKey: qk.stockLedger(productId),
    queryFn: () => stockApi.ledger(productId),
  });
}

export function useBatches(productId: number) {
  return useQuery({
    queryKey: [...qk.stockLedger(productId), 'batches'],
    queryFn: () => stockApi.batches(productId),
  });
}

export function useHistory(productId: number, window: '30d' | '90d' | '365d' = '90d') {
  return useQuery({
    queryKey: [...qk.stockLedger(productId), 'history', window],
    queryFn: () => stockApi.history(productId, window),
  });
}

export function useCompare(productId: number) {
  return useQuery({
    queryKey: [...qk.stockLedger(productId), 'compare'],
    queryFn: () => stockApi.compare(productId),
  });
}

export function usePurchaseOrders(status: string | null, supplierId: string | null) {
  return useQuery({
    queryKey: ['stock', 'po', status ?? 'all', supplierId ?? 'all'],
    queryFn: () => stockApi.pos(status, supplierId),
    ...LIVE,
  });
}

export function usePurchaseOrder(poId: string) {
  return useQuery({
    queryKey: ['stock', 'po', poId],
    queryFn: () => stockApi.po(poId),
    enabled: poId !== '',
  });
}

export function useSuggestions() {
  return useQuery({
    queryKey: ['stock', 'suggestions'],
    queryFn: () => stockApi.suggestions(),
    ...LIVE,
  });
}

export function useSuppliers(includeInactive = false) {
  return useQuery({
    queryKey: ['stock', 'suppliers', includeInactive],
    queryFn: () => stockApi.suppliers(includeInactive),
  });
}

export function useSupplierPrices(productId: number | null, supplierId: string | null) {
  return useQuery({
    queryKey: ['stock', 'supplier-prices', productId ?? 'all', supplierId ?? 'all'],
    queryFn: () => stockApi.supplierPrices(productId, supplierId),
  });
}

export function useSubmitCount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      productId: number;
      counted_qty: number;
      reason: string | null;
      note: string | null;
    }) =>
      stockApi.count(input.productId, {
        counted_qty: input.counted_qty,
        reason: input.reason,
        note: input.note,
        photo_upload_id: null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stock'] });
    },
  });
}

export function useAdvancePo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { poId: string; toStatus: string; reason: string | null }) =>
      stockApi.advancePo(input.poId, input.toStatus, input.reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stock'] });
    },
  });
}

export function useCreatePo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: stockApi.createPo,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stock'] });
    },
  });
}

export function useSnoozeSuggestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { productId: number; days: 7 | 14; note: string | null }) =>
      stockApi.snoozeSuggestion(input.productId, input.days, input.note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stock', 'suggestions'] });
    },
  });
}
